import { useEffect, useMemo, useReducer } from 'react';
import { getBlob, ref } from 'firebase/storage';
import { CollectionSummary, storagePaths } from '@qkindex/contracts';
import { storage } from '../../firebase';
import { Item } from '../../domain/item';
import { createImageBlobCache, imageCacheIdentity } from './imageBlobCache';

// Scoped to one authority and workspace. Completed and in-flight requests survive
// metadata updates, while authority changes fence responses and revoke URLs.
class ImageQueue {
  urls = new Map<string, string>();
  failed = new Set<string>();
  allowed = new Set<string>();
  demands = new Map<string, Map<symbol, number>>();
  callbacks = new Map<string, (priority: number) => () => void>();
  pending = new Set<string>();
  active = false;
  epoch = 0;
  refreshKey: unknown;
  constructor(
    readonly authorityKey: string,
    private notify: () => void,
    private userId: string,
    private collections: CollectionSummary[],
  ) {}
  demand(path: string) {
    let callback = this.callbacks.get(path);
    if (!callback) {
      callback = (priority: number) => {
        const token = Symbol();
        const requests = this.demands.get(path) ?? new Map<symbol, number>();
        requests.set(token, priority);
        this.demands.set(path, requests);
        // Let all visible cards register before selecting the next request.
        void Promise.resolve().then(() => this.pump());
        return () => {
          requests.delete(token);
          if (!requests.size) this.demands.delete(path);
        };
      };
      this.callbacks.set(path, callback);
    }
    return callback;
  }
  update(paths: string[], refreshKey: unknown) {
    this.active = true;
    this.allowed = new Set(paths);
    if (this.refreshKey !== refreshKey) {
      this.failed.clear();
      this.refreshKey = refreshKey;
    }
    this.urls.forEach((url, path) => {
      if (!this.allowed.has(path)) {
        URL.revokeObjectURL(url);
        this.urls.delete(path);
      }
    });
    this.callbacks.forEach((_, path) => {
      if (!this.allowed.has(path)) this.callbacks.delete(path);
    });
    this.notify();
    void Promise.resolve().then(() => this.pump());
  }
  pump() {
    if (!this.active) return;
    while (this.pending.size < 2) {
      const next = [...this.demands.entries()]
        .filter(
          ([path, requests]) =>
            requests.size &&
            this.allowed.has(path) &&
            !this.urls.has(path) &&
            !this.failed.has(path) &&
            !this.pending.has(path),
        )
        .sort(
          (a, b) => Math.min(...a[1].values()) - Math.min(...b[1].values()),
        )[0];
      if (!next) return;
      const path = next[0];
      const epoch = this.epoch;
      this.pending.add(path);
      const collection = this.collections.find((entry) =>
        path.startsWith(`collections/${entry.collectionId}/images/`),
      )!;
      const cache = createImageBlobCache(
        this.userId,
        collection.collectionId,
        imageCacheIdentity(collection),
      );
      const current = () =>
        this.active && epoch === this.epoch && this.allowed.has(path);
      void cache
        .read(path)
        .then(async (cached) => {
          if (!current()) return undefined;
          if (cached) return cached;
          const blob = await getBlob(ref(storage, path), 15 * 1024 * 1024);
          if (current()) void cache.write(path, blob);
          return blob;
        })
        .then((blob) => {
          if (!blob) return;
          if (!this.active || epoch !== this.epoch || !this.allowed.has(path))
            return;
          this.urls.set(path, URL.createObjectURL(blob));
        })
        .catch(() => {
          if (this.active && epoch === this.epoch && this.allowed.has(path))
            this.failed.add(path);
        })
        .finally(() => {
          this.pending.delete(path);
          if (this.active) {
            this.notify();
            this.pump();
          }
        });
    }
  }
  dispose() {
    this.active = false;
    this.epoch += 1;
    this.urls.forEach((url) => URL.revokeObjectURL(url));
    this.urls.clear();
    this.failed.clear();
  }
}

export function useCollectionItemImages(
  userId: string,
  items: Item[],
  collections: CollectionSummary[],
  refreshKey: unknown = items,
): Item[] {
  const [, notify] = useReducer((revision: number) => revision + 1, 0);
  const authorityKey = `${userId}:${JSON.stringify(collections.map((entry) => [entry.collectionId, imageCacheIdentity(entry)]).sort())}`;
  // The serialized key includes every authority field used by this queue.
  const queue = useMemo(
    () => new ImageQueue(authorityKey, notify, userId, collections),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authorityKey],
  );
  const needed = items.flatMap((item) => {
    if (
      item.image ||
      !item.storagePath ||
      !item.collectionId ||
      !collections.some(
        (entry) =>
          entry.collectionId === item.collectionId &&
          ['active', 'transfer-pending'].includes(entry.lifecycle),
      )
    )
      return [];
    const prefix = `collections/${item.collectionId}/images/`;
    try {
      if (
        !item.storagePath.startsWith(prefix) ||
        storagePaths.durableImage(
          item.collectionId,
          item.storagePath.slice(prefix.length),
        ) !== item.storagePath
      )
        return [];
    } catch {
      return [];
    }
    return [item.storagePath];
  });
  const pathKey = JSON.stringify([...new Set(needed)].sort());
  useEffect(() => () => queue.dispose(), [queue]);
  useEffect(() => {
    queue.update(JSON.parse(pathKey), refreshKey);
  }, [queue, pathKey, refreshKey]);
  const neededSet = new Set(needed);
  return items.map((item) =>
    !item.storagePath || !neededSet.has(item.storagePath)
      ? item
      : {
          ...item,
          image: queue.urls.get(item.storagePath) || '',
          imageLoading:
            !queue.urls.has(item.storagePath) &&
            !queue.failed.has(item.storagePath),
          requestImage: queue.demand(item.storagePath),
        },
  );
}
